import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/components/ui/shadcn/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { cn } from '@shared/utils/utils';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useProfitabilitySummaryOrders, useProfitabilitySummaryOrdersFilterOptions } from '../../hooks';
import type { ProfitabilityCommonFilters, ProfitabilitySummaryOrder } from '../../models/Profitability';
import { formatProfitabilityAmount, formatProfitabilityPercent } from '../../utils/formatMoney';

const PER_PAGE = 15;

interface ProfitabilitySummaryOrdersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters?: ProfitabilityCommonFilters;
  locale: string;
}

function buildOrderDetailSearch(filters?: ProfitabilityCommonFilters): string {
  if (!filters?.start_date || !filters?.end_date) return '';
  const params = new URLSearchParams({
    start_date: filters.start_date,
    end_date: filters.end_date,
  });
  if (filters.shop_id != null) params.set('shop_id', String(filters.shop_id));
  return `?${params.toString()}`;
}

function orderStatusLabel(
  row: { status_id: number; status_name?: string | null },
  t: (key: string, fallback?: string) => string,
): string {
  const name = row.status_name?.trim();
  if (name) return name;
  return t(`summary.order_status.${row.status_id}`, String(row.status_id));
}

function optionalLabel(value?: string | null, fallback = '—'): string {
  const trimmed = value?.trim();
  return trimmed || fallback;
}

export const ProfitabilitySummaryOrdersModal = ({
  open,
  onOpenChange,
  filters,
  locale,
}: ProfitabilitySummaryOrdersModalProps) => {
  const { t } = useTranslation('profitabilityModule');
  const currency = useCurrency();
  const [page, setPage] = useState(1);
  const [shopId, setShopId] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [shipmentStatusId, setShipmentStatusId] = useState('');

  const filterOptions = useProfitabilitySummaryOrdersFilterOptions(open);

  useEffect(() => {
    if (!open) {
      setPage(1);
      setShopId('');
      setPaymentMethodId('');
      setShipmentStatusId('');
      return;
    }
    setShopId(filters?.shop_id != null ? String(filters.shop_id) : '');
    setPaymentMethodId('');
    setShipmentStatusId('');
    setPage(1);
  }, [open, filters?.shop_id]);

  useEffect(() => {
    setPage(1);
  }, [shopId, paymentMethodId, shipmentStatusId, filters?.start_date, filters?.end_date]);

  const ordersQueryParams = useMemo(() => {
    if (!filters) return undefined;
    return {
      ...filters,
      shop_id: shopId ? Number(shopId) : undefined,
      payment_method_id: paymentMethodId ? Number(paymentMethodId) : undefined,
      shipment_status_id: shipmentStatusId !== '' ? Number(shipmentStatusId) : undefined,
      page,
      per_page: PER_PAGE,
    };
  }, [filters, shopId, paymentMethodId, shipmentStatusId, page]);

  const query = useProfitabilitySummaryOrders(ordersQueryParams, open);

  const shopOptions = useMemo(
    () => [
      { label: t('summary.summary_orders.all'), value: '' },
      ...(filterOptions.shops.data ?? []).map((shop) => ({
        label: shop.name,
        value: String(shop.id),
      })),
    ],
    [filterOptions.shops.data, t],
  );

  const paymentMethodOptions = useMemo(
    () => [
      { label: t('summary.summary_orders.all'), value: '' },
      ...(filterOptions.paymentMethods.data ?? []).map((method) => ({
        label: method.name,
        value: method.id,
      })),
    ],
    [filterOptions.paymentMethods.data, t],
  );

  const shipmentStatusOptions = useMemo(
    () => [
      { label: t('summary.summary_orders.all'), value: '' },
      { label: t('summary.summary_orders.without_mapping'), value: '0' },
      ...(filterOptions.shipmentStatuses.data ?? []).map((status) => ({
        label: status.name,
        value: String(status.id),
      })),
    ],
    [filterOptions.shipmentStatuses.data, t],
  );

  const rows = query.data?.data ?? [];
  const meta = query.data?.meta;
  const detailSearch = useMemo(() => buildOrderDetailSearch(filters), [filters]);

  const formatDate = (value: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return value;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!y || !mo || !d) return value;
    return new Date(y, mo - 1, d).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-[min(96vw,80rem)] overflow-y-auto border-border bg-background"
        data-testid="profitability-summary-orders-modal"
      >
        <DialogHeader>
          <DialogTitle>{t('summary.summary_orders.title')}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-3" data-testid="profitability-summary-orders-filters">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              {t('summary.summary_orders.filter_shop')}
            </label>
            <Combobox
              options={shopOptions}
              value={shopId}
              setValue={setShopId}
              placeholder={t('summary.summary_orders.all')}
              disabled={filterOptions.shops.isLoading}
              dataTestId="profitability-summary-orders-filter-shop"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              {t('summary.summary_orders.filter_payment_method')}
            </label>
            <Combobox
              options={paymentMethodOptions}
              value={paymentMethodId}
              setValue={setPaymentMethodId}
              placeholder={t('summary.summary_orders.all')}
              disabled={filterOptions.paymentMethods.isLoading}
              dataTestId="profitability-summary-orders-filter-payment-method"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              {t('summary.summary_orders.filter_shipment_status')}
            </label>
            <Combobox
              options={shipmentStatusOptions}
              value={shipmentStatusId}
              setValue={setShipmentStatusId}
              placeholder={t('summary.summary_orders.all')}
              disabled={filterOptions.shipmentStatuses.isLoading}
              dataTestId="profitability-summary-orders-filter-shipment-status"
            />
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">{t('orders.ref')}</TableHead>
                <TableHead className="text-muted-foreground">{t('orders.shop')}</TableHead>
                <TableHead className="text-muted-foreground">{t('orders.status')}</TableHead>
                <TableHead className="text-muted-foreground">{t('orders.payment_method')}</TableHead>
                <TableHead className="text-muted-foreground">{t('orders.shipment_status')}</TableHead>
                <TableHead className="text-muted-foreground">{t('summary.summary_orders.reference_date')}</TableHead>
                <TableHead className="text-right text-muted-foreground">{t('orders.revenue')}</TableHead>
                <TableHead className="text-right text-muted-foreground">{t('orders.cogs')}</TableHead>
                <TableHead className="text-right text-muted-foreground">{t('orders.logistics')}</TableHead>
                <TableHead className="text-right text-muted-foreground">{t('orders.profit')}</TableHead>
                <TableHead className="text-right text-muted-foreground">{t('summary.margin_percent')}</TableHead>
                <TableHead className="w-[48px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i} className="border-border">
                    <TableCell colSpan={12}>
                      <div className="h-8 animate-pulse rounded bg-muted/60" />
                    </TableCell>
                  </TableRow>
                ))}

              {!query.isLoading && query.isError && (
                <TableRow>
                  <TableCell colSpan={12} className="py-10 text-center text-sm text-muted-foreground">
                    {t('errors.generic')}
                  </TableCell>
                </TableRow>
              )}

              {!query.isLoading && !query.isError && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className="py-10 text-center text-sm text-muted-foreground">
                    {t('empty.no_orders')}
                  </TableCell>
                </TableRow>
              )}

              {!query.isLoading &&
                !query.isError &&
                rows.map((row: ProfitabilitySummaryOrder) => (
                  <TableRow key={row.order_id} className="border-border">
                    <TableCell className="font-mono text-sm text-foreground">{row.ref}</TableCell>
                    <TableCell className="max-w-[120px] truncate text-sm text-muted-foreground">
                      {row.shop_name}
                    </TableCell>
                    <TableCell className="max-w-[100px] truncate text-sm text-muted-foreground">
                      {orderStatusLabel(row, t)}
                    </TableCell>
                    <TableCell className="max-w-[120px] truncate text-sm text-muted-foreground">
                      {optionalLabel(row.payment_method_name)}
                    </TableCell>
                    <TableCell className="max-w-[120px] truncate text-sm text-muted-foreground">
                      {optionalLabel(row.shipment_status_name)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {formatDate(row.reference_date)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {formatProfitabilityAmount(row.totals.recognized_revenue, locale, currency)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {formatProfitabilityAmount(row.totals.recognized_cogs, locale, currency)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {formatProfitabilityAmount(row.totals.recognized_logistics, locale, currency)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right tabular-nums text-sm font-medium',
                        row.totals.operating_profit < 0 ? 'text-destructive' : 'text-foreground',
                      )}
                    >
                      {formatProfitabilityAmount(row.totals.operating_profit, locale, currency)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {formatProfitabilityPercent(row.margin_percent, locale)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        asChild
                        title={t('orders.view_detail')}
                        data-testid={`profitability-summary-order-detail-${row.order_id}`}
                      >
                        <Link to={`/reports/profitability/orders/${row.order_id}${detailSearch}`}>
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {meta && meta.total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">{t('pagination.total', { total: meta.total })}</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {t('pagination.page', { current: meta.current_page, last: meta.last_page })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 border-border"
                disabled={meta.current_page <= 1 || query.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                title={t('pagination.prev')}
                data-testid="profitability-summary-orders-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 border-border"
                disabled={meta.current_page >= meta.last_page || query.isFetching}
                onClick={() => setPage((p) => p + 1)}
                title={t('pagination.next')}
                data-testid="profitability-summary-orders-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
